import React, { useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { updateV2Field } from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";

import InstagramIcon from "@/assets/icons/authentication/linkSocial/Instagram.svg";
import LinkedInIcon from "@/assets/icons/authentication/linkSocial/LinkedIn.svg";
import TiktokIcon from "@/assets/icons/authentication/linkSocial/Tiktok.svg";
import BehanceIcon from "@/assets/icons/authentication/linkSocial/Behaance.svg";

import { Plus, Trash2, Users } from "lucide-react";
import YoutubeIcon from "@/assets/icons/authentication/linkSocial/Youtube.svg";
import { validateRequiredFields } from "@/utils/formValidationV2";

export type TSocialPlatform =
  | "instagram"
  | "linkedin"
  | "tiktok"
  | "behance"
  | "youtube"
  | "facebook"
  | "x"
  | "website"
  | "custom";

export type TProfileLinkItem = {
  id: string;
  platform: TSocialPlatform;
  label?: string;
  url: string;
  followers?: number | null;
};

const DEFAULT_PLATFORMS = [
  { platform: "youtube", placeholder: "Paste your YouTube profile (Optional)" },
  {
    platform: "linkedin",
    placeholder: "Paste your LinkedIn profile (Optional)",
  },
  {
    platform: "instagram",
    placeholder: "Paste your Instagram profile (Optional)",
  },
  { platform: "tiktok", placeholder: "Paste your TikTok profile (Optional)" },
] satisfies { platform: TSocialPlatform; placeholder: string }[];

const PLATFORM_META: Record<
  TSocialPlatform,
  { label: string; icon?: React.ReactNode; placeholder?: string }
> = {
  youtube: { label: "Youtube", icon: <YoutubeIcon /> },
  linkedin: { label: "LinkedIn", icon: <LinkedInIcon /> },
  instagram: { label: "Instagram", icon: <InstagramIcon /> },
  tiktok: { label: "TikTok", icon: <TiktokIcon /> },
  behance: { label: "Behance", icon: <BehanceIcon /> },
  facebook: { label: "Facebook" },
  x: { label: "X (Twitter)" },
  website: { label: "Website" },
  custom: { label: "Custom" },
};

function uid() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function ensureDefaults(existing: TProfileLinkItem[]): TProfileLinkItem[] {
  const byPlatform = new Map(existing.map((i) => [i.platform, i]));
  const defaults = DEFAULT_PLATFORMS.map(({ platform }) => {
    const found = byPlatform.get(platform);
    return found ?? { id: uid(), platform, url: "", followers: null };
  });
  const extras = existing.filter(
    (i) => !DEFAULT_PLATFORMS.some((d) => d.platform === i.platform),
  );
  return [...defaults, ...extras];
}

function normalizeUrl(raw: string) {
  const v = raw.trim();
  if (!v) return "";
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  if (v.includes(".") || v.includes("/")) return `https://${v}`;
  return v;
}

function parseFollowers(v: string): number | null {
  const cleaned = v.replace(/,/g, "").trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

// ── exported for ApplicationStep4 Proceed button ──
export function validateProfileLinks(links: TProfileLinkItem[]) {
  const hasAnyLink = links.some((l) => l.url?.trim());

  return validateRequiredFields(
    { profileLinksAny: hasAnyLink ? "ok" : "" },
    ["profileLinksAny"],
  );
}

const ProfileLinksSection = ({ forceValidate = false }: { forceValidate?: boolean }) => {
  const dispatch = useAppDispatch();
  const storeLinks =
    useAppSelector((s) => s.signUpV2?.v2Extras?.profileLinks) ?? [];
  const links = useMemo(() => ensureDefaults(storeLinks), [storeLinks]);

  const [followersOpen, setFollowersOpen] = useState<Record<string, boolean>>(
    {},
  );
  const [addOpen, setAddOpen] = useState(false);
  const [newPlatform, setNewPlatform] = useState<TSocialPlatform>("youtube");
  const [customLabel, setCustomLabel] = useState("");

  // show error only after user has interacted
  const [touched, setTouched] = useState(false);

  const hasAnyLink = links.some((l) => l.url?.trim());
  const profileLinksError =
    (touched || forceValidate) && !hasAnyLink ? "At least one profile link is required" : undefined;

  const commit = (next: TProfileLinkItem[]) => {
    dispatch(updateV2Field("profileLinks", next));
  };

  const updateItem = (id: string, patch: Partial<TProfileLinkItem>) => {
    commit(links.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id: string) => {
    const isDefault = DEFAULT_PLATFORMS.some(
      (d) => links.find((l) => l.id === id)?.platform === d.platform,
    );
    if (isDefault) return;

    const next = links.filter((it) => it.id !== id);
    commit(next);

    setTouched(true);

    setFollowersOpen((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
  };

  const alreadyHasPlatform = (p: TSocialPlatform) =>
    links.some((l) => l.platform === p);

  const addAnother = () => {
    if (newPlatform !== "custom" && alreadyHasPlatform(newPlatform)) {
      setAddOpen(false);
      return;
    }

    const item: TProfileLinkItem = {
      id: uid(),
      platform: newPlatform,
      label:
        newPlatform === "custom" ? customLabel.trim() || "Other" : undefined,
      url: "",
      followers: null,
    };

    commit([...links, item]);
    setTouched(true);
    setAddOpen(false);
    setCustomLabel("");
    setNewPlatform("youtube");
  };

  return (
    <div className="w-full max-w-md" id="profileLinksAny">
      <div className="mb-3">
        <label className="text-sm font-medium text-[#121217]">
          Profile links
        </label>
        <p className="text-[10px] text-[#475467] mt-0.5">
          Add at least one profile link. Brands shortlist creators by skills,
          not followers. 😌
        </p>
      </div>

      <div className="space-y-2">
        {links.map((item, idx) => {
          const meta = PLATFORM_META[item.platform];
          const placeholder =
            DEFAULT_PLATFORMS.find((d) => d.platform === item.platform)
              ?.placeholder ?? `Paste your ${meta.label} link`;

          const showRemove = !DEFAULT_PLATFORMS.some(
            (d) => d.platform === item.platform,
          );

          return (
            <div key={item.id}>
              <ProfileLinkRow
                isLast={idx === links.length - 1}
                icon={meta.icon}
                title={
                  item.platform === "custom"
                    ? (item.label ?? "Other")
                    : meta.label
                }
                placeholder={placeholder}
                urlValue={item.url}
                followersValue={item.followers ?? null}
                followersOpen={!!followersOpen[item.id]}
                hasSectionError={!!profileLinksError}
                onToggleFollowers={() =>
                  setFollowersOpen((p) => ({ ...p, [item.id]: !p[item.id] }))
                }
                onUrlChange={(v) => {
                  setTouched(true);
                  updateItem(item.id, { url: normalizeUrl(v) });
                }}
                onBlur={() => {
                  setTouched(true);
                }}
                onFollowersChange={(v) =>
                  updateItem(item.id, { followers: parseFollowers(v) })
                }
                onRemove={showRemove ? () => removeItem(item.id) : undefined}
              />
            </div>
          );
        })}
      </div>

      {profileLinksError && (
        <p className="text-[10px] text-red-500 pl-1 mt-1">
          {profileLinksError}
        </p>
      )}

      {/* Add another */}
      <div className="mt-3">
        {!addOpen ? (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 text-xs font-medium text-[#FF6600]"
          >
            <Plus size={14} />
            Add another social link
          </button>
        ) : (
          <div className="mt-2 rounded-xl border border-[#EAECF0] bg-white p-3">
            <div className="flex items-center gap-2">
              <select
                className="w-full rounded-lg border border-[#EAECF0] px-3 py-2 text-xs outline-none focus:border-[#FF6600]"
                value={newPlatform}
                onChange={(e) =>
                  setNewPlatform(e.target.value as TSocialPlatform)
                }
              >
                <option
                  value="youtube"
                  disabled={alreadyHasPlatform("youtube")}
                >
                  YouTube
                </option>
                <option
                  value="facebook"
                  disabled={alreadyHasPlatform("facebook")}
                >
                  Facebook
                </option>
                <option value="x" disabled={alreadyHasPlatform("x")}>
                  X (Twitter)
                </option>
                <option
                  value="website"
                  disabled={alreadyHasPlatform("website")}
                >
                  Website
                </option>
                <option value="custom">Custom</option>
              </select>

              <button
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  setCustomLabel("");
                }}
                className="rounded-lg border border-[#EAECF0] px-3 py-2 text-xs text-[#475467]"
              >
                Cancel
              </button>
            </div>

            {newPlatform === "custom" && (
              <div className="mt-2">
                <input
                  className="w-full rounded-lg border border-[#EAECF0] px-3 py-2 text-xs outline-none focus:border-[#FF6600]"
                  placeholder="Custom platform name (e.g. Snapchat)"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                />
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={addAnother}
                className="rounded-full bg-[#FF6600] px-4 py-2 text-xs font-semibold text-white"
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileLinksSection;

// ─────────────────────────────────────────────
// ProfileLinkRow
// ─────────────────────────────────────────────
function ProfileLinkRow({
  icon,
  title,
  placeholder,
  urlValue,
  followersValue,
  onUrlChange,
  onBlur,
  onFollowersChange,
  onRemove,
  hasSectionError,
}: {
  icon?: React.ReactNode;
  title: string;
  placeholder: string;
  urlValue: string;
  followersValue: number | null;
  followersOpen?: boolean;
  onToggleFollowers?: () => void;
  onUrlChange: (v: string) => void;
  onBlur?: () => void;
  onFollowersChange: (v: string) => void;
  onRemove?: () => void;
  isLast?: boolean;
  hasSectionError?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const openEdit = () => {
    setDraft(followersValue ? String(followersValue) : "");
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitFollowers = () => {
    onFollowersChange(draft);
    setTimeout(() => setEditing(false), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") commitFollowers();
  };

  const hasError = hasSectionError && !urlValue?.trim();

  return (
    <div className="space-y-1">
      <div
        className={`rounded-xl border bg-white overflow-hidden transition-colors duration-150
          ${hasError ? "border-red-400" : "border-[#EAECF0]"}`}
      >
        <div className="flex items-stretch">
          <div
            className={`flex w-11 items-center justify-center border-r bg-[#F9FAFB] relative
              ${hasError ? "border-red-200" : "border-[#EAECF0]"}`}
          >
            {icon ?? (
              <div className="text-[10px] text-[#98A2B3]">{title[0]}</div>
            )}
          </div>

          <div className="flex flex-1 items-center gap-2 px-3 py-2.5">
            <div className="flex-1 min-w-0">
              <input
                value={urlValue}
                onChange={(e) => onUrlChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                className="w-full text-xs text-[#121217] placeholder:text-[#98A2B3] outline-none"
              />
            </div>

            {editing ? (
              <div className="flex items-center gap-1.5 rounded-lg border border-[#EAECF0] bg-[#F9FAFB] px-2.5 py-1 shrink-0">
                <Users size={11} className="text-[#98A2B3]" />
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
                  onBlur={commitFollowers}
                  onKeyDown={handleKeyDown}
                  inputMode="numeric"
                  placeholder="e.g. 12000"
                  className="w-10 md:w-20 bg-transparent text-[10px] text-[#121217] outline-none placeholder:text-[#98A2B3]"
                />
              </div>
            ) : followersValue ? (
              <button
                type="button"
                onClick={openEdit}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#FFE6D5] bg-[#FFF4EC] px-2.5 py-1 text-[10px] font-semibold text-[#FF6600] cursor-pointer"
              >
                <Users size={11} />
                {followersValue.toLocaleString()}
              </button>
            ) : (
              <button
                type="button"
                onClick={openEdit}
                className="shrink-0 text-[11px] font-semibold text-[#FF6600] hover:opacity-75 transition-opacity border border-[#EDEDED] px-[10px] py-[2px] rounded-full"
              >
                +Add Followers
              </button>
            )}

            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="shrink-0 rounded-full border border-[#EAECF0] p-1.5 text-[#667085] hover:bg-[#F9FAFB] transition-colors"
                aria-label="Remove"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
